
import React, { useState } from 'react';
import { QuoteData, QuoteItem, FeeType, BillingCadence } from '../types';
import { professionalizeDescription } from '../services/geminiService';
import { PlusIcon, TrashIcon, SparklesIcon } from './Icons';

interface QuoteFormProps {
  quote: QuoteData;
  setQuote: React.Dispatch<React.SetStateAction<QuoteData>>;
}

export const QuoteForm: React.FC<QuoteFormProps> = ({ quote, setQuote }) => {
  const [loadingId, setLoadingId] = useState<string | null>(null);

  const updateClient = (field: string, value: string) => {
    setQuote(prev => ({
      ...prev,
      client: { ...prev.client, [field]: value }
    }));
  };

  const updateSender = (field: string, value: string) => {
    setQuote(prev => ({
      ...prev,
      sender: { ...prev.sender, [field]: value }
    }));
  };

  const addItem = () => {
    const newItem: QuoteItem = {
      id: Math.random().toString(36).substr(2, 9),
      name: '',
      description: '',
      price: 0,
      quantity: 1,
      type: FeeType.MONTHLY
    };
    setQuote(prev => ({ ...prev, items: [...prev.items, newItem] }));
  };

  const updateItem = (id: string, field: keyof QuoteItem, value: any) => {
    setQuote(prev => ({
      ...prev,
      items: prev.items.map(item => item.id === id ? { ...item, [field]: value } : item)
    }));
  };

  const removeItem = (id: string) => {
    setQuote(prev => ({
      ...prev,
      items: prev.items.filter(item => item.id !== id)
    }));
  };

  const handleProfessionalize = async (id: string, text: string) => {
    setLoadingId(id);
    const newText = await professionalizeDescription(text);
    updateItem(id, 'description', newText);
    setLoadingId(null);
  };

  return (
    <div className="space-y-8 bg-white p-6 rounded-2xl shadow-sm border border-slate-200">
      <section>
        <h3 className="text-lg font-semibold text-slate-800 mb-4 border-b pb-2">Information</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Mottagare (Kund)</h4>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Företag</label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                value={quote.client.companyName}
                onChange={(e) => updateClient('companyName', e.target.value)}
                placeholder="Ex: Kund AB"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Kontaktperson</label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                value={quote.client.contactPerson}
                onChange={(e) => updateClient('contactPerson', e.target.value)}
                placeholder="Ex: Johan Andersson"
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Adress</label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                value={quote.client.address}
                onChange={(e) => updateClient('address', e.target.value)}
                placeholder="Gata, Postnummer, Ort"
              />
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Avsändare (Frostpil)</h4>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Ditt Namn</label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                value={quote.sender.name}
                onChange={(e) => updateSender('name', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Mobilnummer</label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                value={quote.sender.phone}
                onChange={(e) => updateSender('phone', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">E-post</label>
              <input
                type="text"
                className="w-full px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                value={quote.sender.email}
                onChange={(e) => updateSender('email', e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      <section>
        <div className="flex justify-between items-end mb-4 border-b pb-2">
          <h3 className="text-lg font-semibold text-slate-800">Tjänster & Produkter</h3>
          <button
            onClick={addItem}
            className="flex items-center gap-1 text-sm bg-blue-600 hover:bg-blue-700 text-white px-3 py-1.5 rounded-lg transition-colors shadow-sm"
          >
            <PlusIcon className="w-4 h-4" /> Lägg till rad
          </button>
        </div>

        <div className="space-y-6">
          {quote.items.map((item) => (
            <div key={item.id} className="p-4 bg-slate-50 rounded-xl border border-slate-200 relative group animate-in fade-in slide-in-from-top-2">
              <button
                onClick={() => removeItem(item.id)}
                className="absolute -top-2 -right-2 bg-white border border-slate-200 text-red-500 p-1.5 rounded-full shadow-sm hover:bg-red-50 transition-colors"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
              
              <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                <div className="md:col-span-6">
                  <label className="block text-[10px] font-medium text-slate-500 mb-1 uppercase tracking-wider">Benämning</label>
                  <input
                    type="text"
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg outline-none"
                    value={item.name}
                    onChange={(e) => updateItem(item.id, 'name', e.target.value)}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-[10px] font-medium text-slate-500 mb-1 uppercase tracking-wider">Typ</label>
                  <select
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg outline-none font-medium"
                    value={item.type}
                    onChange={(e) => updateItem(item.id, 'type', e.target.value as FeeType)}
                  >
                    <option value={FeeType.FIXED}>Engångsavgift</option>
                    <option value={FeeType.MONTHLY}>Månadsavgift</option>
                    <option value={FeeType.YEARLY}>Årsavgift</option>
                  </select>
                </div>
                <div className="md:col-span-3">
                  <label className="block text-[10px] font-medium text-slate-500 mb-1 uppercase tracking-wider">Antal</label>
                  <input
                    type="number"
                    min="1"
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg outline-none"
                    value={item.quantity}
                    onChange={(e) => updateItem(item.id, 'quantity', Number(e.target.value))}
                  />
                </div>
                <div className="md:col-span-9">
                  <label className="flex justify-between items-center text-[10px] font-medium text-slate-500 mb-1 uppercase tracking-wider">
                    Beskrivning
                    <button
                      onClick={() => handleProfessionalize(item.id, item.description)}
                      disabled={loadingId === item.id || !item.description}
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 disabled:opacity-50 lowercase tracking-normal font-bold"
                    >
                      <SparklesIcon className={`w-3 h-3 ${loadingId === item.id ? 'animate-spin' : ''}`} />
                      {loadingId === item.id ? 'Fixar...' : 'Snygga till med AI'}
                    </button>
                  </label>
                  <textarea
                    className="w-full px-3 py-1.5 bg-white border border-slate-200 rounded-lg outline-none min-h-[60px]"
                    value={item.description}
                    onChange={(e) => updateItem(item.id, 'description', e.target.value)}
                  />
                </div>
                <div className="md:col-span-3">
                  <label className="block text-[10px] font-medium text-blue-700 mb-1 uppercase tracking-wider font-bold">Pris / {item.type === FeeType.MONTHLY ? 'mån' : item.type === FeeType.YEARLY ? 'år' : 'st'}</label>
                  <input
                    type="number"
                    className="w-full px-3 py-1.5 bg-blue-50 border border-blue-200 rounded-lg outline-none font-bold text-blue-900"
                    value={item.price}
                    onChange={(e) => updateItem(item.id, 'price', Number(e.target.value))}
                  />
                </div>
              </div>
            </div>
          ))}
          {quote.items.length === 0 && (
            <div className="text-center py-8 text-slate-400 border-2 border-dashed border-slate-200 rounded-xl italic text-sm">
              Inga rader tillagda ännu.
            </div>
          )}
        </div>
      </section>

      <section>
        <h3 className="text-lg font-semibold text-slate-800 mb-4 border-b pb-2">Inställningar</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Faktureringscykel</label>
            <select
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-bold"
              value={quote.billingCadence}
              onChange={(e) => setQuote(prev => ({ ...prev, billingCadence: e.target.value as BillingCadence }))}
            >
              {Object.values(BillingCadence).map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-[10px] font-medium text-slate-500 uppercase tracking-wider mb-1">Giltighetstid</label>
            <input
              type="date"
              className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              value={quote.validUntil}
              onChange={(e) => setQuote(prev => ({ ...prev, validUntil: e.target.value }))}
            />
          </div>
        </div>
      </section>
    </div>
  );
};
